import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useDeferredValue } from 'react'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

/**
 * - { type: 'divider' } (any nullish value will be treated as a divider)
 * - { type: 'header', label }
 * - { type: 'menu', label, icon, items, disabled, tooltip, sublabel, showSelectAll }
 * - { type: 'boolean', label, icon, value, onChange, disabled, keepOpen, tooltip, sublabel }
 * - { type: 'action', label, icon, onClick, value, disabled, keepOpen, tooltip, sublabel, shortcut }
 */
export const normalizeRibbonMenuItems = (items) => {
  return (items || []).map((it) => {
    if (!it) return { type: 'divider' }
    if (it.type) return it

    const hasBool = typeof it.value === 'boolean' || typeof it.checked === 'boolean' || typeof it.isOn === 'boolean'
    const hasOnChange = typeof it.onChange === 'function'

    if (hasBool && hasOnChange) return { type: 'boolean', ...it }
    if (typeof it.onClick === 'function') return { type: 'action', ...it }

    return { type: 'action', ...it }
  })
}

function containsText(text, query) {
  return String(text).toLowerCase().indexOf(String(query).toLowerCase()) > -1
}

function filterItems(items, query) {
  if (!query) return items
  const result = []
  let lastHeader = null
  let headerAdded = false
  let headerMatches = false

  for (let i = 0; i < items.length; i++) {
    const it = items[i]

    if (it.type === 'divider') continue

    if (it.type === 'header') {
      lastHeader = it
      headerAdded = false
      headerMatches = containsText(it.label, query)
      if (headerMatches) {
        result.push(it)
        headerAdded = true
      }
      continue
    }

    if (headerMatches) {
      result.push(it)
      continue
    }

    if (it.type === 'menu') {
      const filteredChildren = filterItems(it.items || [], query)
      if (filteredChildren.length > 0) {
        if (lastHeader && !headerAdded) {
          result.push(lastHeader)
          headerAdded = true
        }
        result.push({ ...it, items: filteredChildren })
        continue
      }
    }

    if (!containsText(it.label ?? '', query)) continue

    if (lastHeader && !headerAdded) {
      result.push(lastHeader)
      headerAdded = true
    }
    result.push(it)
  }
  return result
}

function collectBooleanStates(items) {
  let total = 0
  let checked = 0
  for (let i = 0; i < items.length; i++) {
    const it = items[i]
    if (it.type === 'boolean' && !it.disabled) {
      total++
      if (it.value) checked++
    } else if (it.type === 'menu' && it.items) {
      const sub = collectBooleanStates(it.items)
      total += sub.total
      checked += sub.checked
    }
  }
  return { total, checked }
}

function toggleAllBooleans(items, selectAll) {
  for (let i = 0; i < items.length; i++) {
    const it = items[i]
    if (it.type === 'boolean' && !it.disabled && it.value !== selectAll) {
      it.onChange?.(selectAll)
    } else if (it.type === 'menu' && it.items) {
      toggleAllBooleans(it.items, selectAll)
    }
  }
}

function SubMenuItem({ item, minWidth, paperSx, backColor, borderColor, text, closeEverything, renderItems }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [flipped, setFlipped] = useState(false)
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)
  const closeTimer = useRef(null)
  const searchInputRef = useRef(null)

  const open = Boolean(anchorEl)

  const subitems = useMemo(() => normalizeRibbonMenuItems(item.items || []), [item.items])
  const displayedItems = useMemo(() => {
    if (!item.searchable || !deferredSearchText) return subitems
    return filterItems(subitems, deferredSearchText)
  }, [subitems, item.searchable, deferredSearchText])

  useEffect(() => {
    if (!open || !item.searchable) return
    const id = setTimeout(() => {
      searchInputRef.current?.focus()
      searchInputRef.current?.select()
    }, 50)
    return () => clearTimeout(id)
  }, [open, item.searchable])

  useEffect(() => {
    if (!open) setSearchText('')
  }, [open])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setAnchorEl(null), 120)
  }, [cancelClose])

  const handleEnter = useCallback(
    (e) => {
      if (item.disabled) return
      cancelClose()
      setAnchorEl(e.currentTarget)
      const rect = e.currentTarget.getBoundingClientRect()
      setFlipped(window.innerWidth - rect.right < minWidth)
    },
    [item.disabled, minWidth, cancelClose],
  )

  const closeThisMenu = useCallback(() => setAnchorEl(null), [])

  const subSearchBar = item.searchable ? (
    <ListSubheader sx={{ backgroundColor: backColor, px: 1, py: 0.75, lineHeight: 'normal', zIndex: 1 }}>
      <TextField
        variant='outlined'
        size='small'
        inputRef={searchInputRef}
        placeholder={item.searchPlaceholder ?? 'Search...'}
        fullWidth
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') e.stopPropagation()
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon sx={{ color: text, fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputBase-root': { backgroundColor: backColor, color: text, fontSize: 13 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#429CEB' },
        }}
      />
    </ListSubheader>
  ) : null

  const subSelectAllBar = item.showSelectAll
    ? (() => {
        const { total, checked } = collectBooleanStates(subitems)
        const allSelected = total > 0 && checked === total
        const indeterminate = checked > 0 && !allSelected

        return (
          <MenuItem
            onClick={() => toggleAllBooleans(subitems, !allSelected)}
            sx={{ gap: 0.5, borderBottom: `1px solid ${borderColor}` }}>
            <Checkbox
              checked={allSelected}
              indeterminate={indeterminate}
              tabIndex={-1}
              disableRipple
              size='small'
              sx={{ p: 0, mr: 1, color: !allSelected ? text : undefined }}
            />
            <ListItemText
              primary='Select All'
              primaryTypographyProps={{ variant: 'body2', color: text, fontWeight: 600 }}
            />
          </MenuItem>
        )
      })()
    : null

  const subClearBar = item.clearable ? (
    <Box
      onClick={() => {
        const clearBooleans = (list) => {
          for (let i = 0; i < list.length; i++) {
            const it = list[i]
            if (it.type === 'boolean' && !it.disabled && it.value) {
              it.onChange?.(false)
            } else if (it.type === 'menu' && it.items) {
              clearBooleans(it.items)
            }
          }
        }
        clearBooleans(subitems)
      }}
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        backgroundColor: backColor,
        borderTop: `1px solid ${borderColor}`,
        px: 1.5,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        cursor: 'pointer',
        userSelect: 'none',
      }}>
      <ClearIcon sx={{ color: text, fontSize: 16, opacity: 0.7, display: 'block' }} />
      <Typography variant='caption' sx={{ color: text, opacity: 0.85, lineHeight: 1 }}>
        Clear
      </Typography>
    </Box>
  ) : null

  const itemIcon = item.icon ? <ListItemIcon sx={{ minWidth: 34, color: text }}>{item.icon}</ListItemIcon> : null
  const sublabelNode = item.sublabel ? (
    <Typography variant='caption' sx={{ opacity: 0.6, display: 'block', lineHeight: 1.2, color: text }}>
      {item.sublabel}
    </Typography>
  ) : null

  const handleClick = useCallback(() => {
    if (item.disabled || typeof item.onClick !== 'function') return

    cancelClose()
    setAnchorEl(null)

    item.onClick(item.value)

    if (!item.keepOpen) closeEverything()

    document.activeElement?.blur?.()
  }, [item, cancelClose, closeEverything])

  const row = (
    <MenuItem
      disabled={!!item.disabled}
      onClick={typeof item.onClick === 'function' ? handleClick : undefined}
      onMouseEnter={handleEnter}
      onMouseLeave={scheduleClose}
      sx={{ gap: 0.5, color: text }}>
      {itemIcon}

      <ListItemText
        primary={item.label}
        secondary={sublabelNode}
        primaryTypographyProps={{ variant: 'body2', color: text }}
      />

      <Typography variant='caption' sx={{ opacity: 0.7, ml: 2, color: text }}>
        ▶
      </Typography>
    </MenuItem>
  )

  return (
    <>
      {item.tooltip ? (
        <Tooltip title={item.tooltip} placement='left'>
          <Box>{row}</Box>
        </Tooltip>
      ) : (
        row
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeThisMenu}
        disableAutoFocusItem
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'top', horizontal: flipped ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: flipped ? 'right' : 'left' }}
        slotProps={{
          paper: {
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
            sx: { pointerEvents: 'auto', ...paperSx },
          },
        }}>
        {subSearchBar}
        {subSelectAllBar}
        {renderItems(displayedItems, { closeThisMenu, closeEverything })}
        {subClearBar}
      </Menu>
    </>
  )
}

export default function RibbonDropdownMenu({
  anchorEl,
  anchorReference = 'anchorEl',
  anchorPosition,
  open,
  onClose,

  items = [],

  maxHeight = 500,
  minWidth = 220,
  borderRadius = 2,
  borderColorOverride,
  searchable = false,
  searchPlaceholder = 'Search...',
  clearable = false,
}) {
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const searchInputRef = useRef(null)

  const { ribbonStyle } = useInnovaTheme()
  const text = ribbonStyle.color

  const normalizedItems = useMemo(() => normalizeRibbonMenuItems(items), [items])

  const closeAllMenus = useCallback(() => {
    onClose?.()
  }, [onClose])

  const handleClose = useCallback(() => {
    setSearchText('')
    closeAllMenus()
  }, [closeAllMenus])

  useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      const id = setTimeout(() => {
        searchInputRef.current?.focus()
        searchInputRef.current?.select()
      }, 50)
      return () => clearTimeout(id)
    }
  }, [open, searchable])

  useEffect(() => {
    if (!open) {
      setSearchText('')
    }
  }, [open])

  const borderColor = useMemo(() => {
    if (borderColorOverride) return borderColorOverride
    return ribbonStyle.defaultBorderColor
  }, [borderColorOverride, ribbonStyle])

  const backColor = ribbonStyle.backgroundColor

  const displayedItems = useMemo(() => {
    if (!searchable || !deferredSearchText) return normalizedItems
    return filterItems(normalizedItems, deferredSearchText)
  }, [normalizedItems, searchable, deferredSearchText])

  const handleClearAll = useCallback(() => {
    const clearBooleans = (list) => {
      for (let i = 0; i < list.length; i++) {
        const it = list[i]
        if (it.type === 'boolean' && !it.disabled && it.value) {
          it.onChange?.(false)
        } else if (it.type === 'menu' && it.items) {
          clearBooleans(it.items)
        }
      }
    }
    clearBooleans(normalizedItems)
  }, [normalizedItems])

  const paperSx = useMemo(
    () => ({
      backgroundColor: backColor,
      borderRadius,
      minWidth,
      maxHeight,
      overflowY: 'auto',
    }),
    [backColor, borderRadius, minWidth, maxHeight],
  )

  const renderMenuItems = useCallback(
    (menuItems, { closeThisMenu, closeEverything }) => {
      const normalized = normalizeRibbonMenuItems(menuItems)

      if (normalized.length === 0) {
        return (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant='caption' sx={{ opacity: 0.5, color: text }}>
              No items
            </Typography>
          </Box>
        )
      }

      return normalized.map((it, idx) => {
        if (it.type === 'divider') return <Divider key={`div-${idx}`} sx={{ borderColor }} />

        if (it.type === 'header') {
          return (
            <Box key={`hdr-${idx}`} sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
              <Typography variant='caption' sx={{ opacity: 0.75, fontWeight: 800, letterSpacing: 0.3, color: text }}>
                {it.label}
              </Typography>
            </Box>
          )
        }

        const itemIcon = it.icon ? <ListItemIcon sx={{ minWidth: 34, color: text }}>{it.icon}</ListItemIcon> : null
        const sublabelNode = it.sublabel ? (
          <Typography variant='caption' sx={{ opacity: 0.6, display: 'block', lineHeight: 1.2, color: text }}>
            {it.sublabel}
          </Typography>
        ) : null

        if (it.type === 'menu') {
          return (
            <SubMenuItem
              key={it.key ?? it.label ?? `menu-${idx}`}
              item={it}
              minWidth={minWidth}
              paperSx={paperSx}
              backColor={backColor}
              borderColor={borderColor}
              text={text}
              closeEverything={closeEverything}
              renderItems={renderMenuItems}
            />
          )
        }

        if (it.type === 'boolean') {
          const checked = it.value

          const handleToggle = () => {
            if (it.disabled) return
            const next = !checked

            it.onChange?.(next)

            if (!it.keepOpen) closeThisMenu()
            document.activeElement?.blur?.()
          }

          return (
            <MenuItem
              key={it.key ?? it.label ?? `bool-${idx}`}
              disabled={!!it.disabled}
              onClick={handleToggle}
              sx={{ gap: 0.5 }}>
              {itemIcon}
              <Checkbox
                checked={!!checked}
                indeterminate={checked === null}
                tabIndex={-1}
                disableRipple
                size='small'
                sx={{ p: 0, mr: 1, color: !checked ? text : undefined }}
              />
              <ListItemText
                primary={it.label}
                secondary={sublabelNode ? <>{sublabelNode}</> : null}
                primaryTypographyProps={{ variant: 'body2', color: text }}
              />
            </MenuItem>
          )
        }

        if (it.type === 'custom') {
          return (
            <Box
              key={it.key ?? `custom-${idx}`}
              onClick={(e) => {
                if (it.keepOpen) {
                  e.stopPropagation()
                }
              }}
              onMouseDown={(e) => {
                if (it.keepOpen) {
                  e.stopPropagation()
                }
              }}
              sx={{
                px: it.disablePadding ? 0 : 1.25,
                py: it.disablePadding ? 0 : 1,
              }}>
              {typeof it.content === 'function'
                ? it.content({
                    closeMenu: !it.keepOpen ? closeEverything : closeThisMenu,
                    closeThisMenu,
                  })
                : it.content}
            </Box>
          )
        }

        if (it.type !== 'action') return null

        const handleAction = () => {
          if (it.disabled) return
          it.onClick?.(it.value)
          if (!it.keepOpen) closeEverything()
          document.activeElement?.blur?.()
        }

        const row = (
          <MenuItem
            key={it.key ?? it.value ?? it.label ?? `act-${idx}`}
            disabled={!!it.disabled}
            onClick={handleAction}
            sx={{ gap: 0.5 }}>
            {itemIcon}

            <ListItemText
              primary={it.label}
              secondary={sublabelNode ? <>{sublabelNode}</> : null}
              primaryTypographyProps={{ variant: 'body2', color: text }}
            />

            {it.shortcut ? (
              <Typography variant='caption' sx={{ opacity: 0.7, ml: 2, color: text }}>
                {it.shortcut}
              </Typography>
            ) : null}

            {it.actions?.length ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', ml: 1 }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}>
                {it.actions.map((action) => (
                  <Tooltip key={action.key} title={action.tooltip ?? ''} placement='top'>
                    <span>
                      <IconButton
                        size='small'
                        disabled={action.disabled}
                        onClick={(e) => {
                          e.stopPropagation()
                          action.onClick?.(it)
                        }}
                        sx={{
                          p: 0.5,
                          color: text,
                          opacity: 0.7,
                          '&:hover': {
                            opacity: 1,
                          },
                        }}>
                        {action.icon}
                      </IconButton>
                    </span>
                  </Tooltip>
                ))}
              </Box>
            ) : null}
          </MenuItem>
        )

        return it.tooltip ? (
          <Tooltip key={`tt-${idx}`} title={it.tooltip} placement='left'>
            <Box>{row}</Box>
          </Tooltip>
        ) : (
          row
        )
      })
    },
    [minWidth, borderColor, paperSx, text, backColor],
  )

  const searchBar = useMemo(() => {
    if (!searchable) return null
    return (
      <ListSubheader sx={{ backgroundColor: backColor, px: 1, py: 0.75, lineHeight: 'normal', zIndex: 1 }}>
        <TextField
          variant='outlined'
          size='small'
          inputRef={searchInputRef}
          placeholder={searchPlaceholder}
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') e.stopPropagation()
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ color: text, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: backColor, color: text, fontSize: 13 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#429CEB' },
          }}
        />
      </ListSubheader>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchable, backColor, searchPlaceholder, text, borderColor, searchText])

  const clearBar = useMemo(() => {
    if (!clearable) return null
    return (
      <Box
        onClick={handleClearAll}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          backgroundColor: backColor,
          borderTop: `1px solid ${borderColor}`,
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
        }}>
        <ClearIcon sx={{ color: text, fontSize: 16, opacity: 0.7 }} />
        <Typography variant='caption' sx={{ color: text, opacity: 0.85 }}>
          Clear
        </Typography>
      </Box>
    )
  }, [clearable, backColor, borderColor, text, handleClearAll])

  const emptyState =
    searchable && deferredSearchText && displayedItems.length === 0 ? (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant='caption' sx={{ opacity: 0.5, color: text }}>
          No results for "{deferredSearchText}"
        </Typography>
      </Box>
    ) : null

  const hasCustomContent = useMemo(() => normalizedItems.some((item) => item.type === 'custom'), [normalizedItems])

  return (
    <>
      <Menu
        anchorReference={anchorReference}
        anchorEl={anchorReference === 'anchorEl' ? anchorEl : undefined}
        anchorPosition={anchorReference === 'anchorPosition' ? anchorPosition : undefined}
        open={open}
        onClose={handleClose}
        disableAutoFocusItem
        disableEnforceFocus={hasCustomContent}
        slotProps={{
          paper: {
            sx: paperSx,
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        {searchBar}
        {emptyState}
        {renderMenuItems(displayedItems, {
          closeThisMenu: onClose,
          closeEverything: handleClose,
        })}
        {clearBar}
      </Menu>
    </>
  )
}
